import type { AbsentReason, ArtifactState, SessionId } from "@don/wire";

import { gapText, timecode, type SessionGap } from "./timeline.js";

/**
 * The reading column: the rail, the transcript, the notes, and the export.
 *
 * Pure, and none of it lives in a component, for the reason `timeline.ts` is not a
 * `useMemo`: the product rules on this surface are what is most worth asserting, and a
 * rule that exists only inside a render function can only be asserted through a browser.
 *
 * Three rules are the reason this file exists:
 *
 * 1. **The rail has exactly four kinds.** `docs/prds/26-09-15-v0-capture-decisions.md`
 *    §(g) cut owner-private notes for V0: the design bundle ships a fifth `PRIVATE` kind
 *    with a dashed rule, and no user story, spec line or schema mentions it. So the
 *    vocabulary is closed, membership is a walk over it, and a fifth kind is refused
 *    rather than given a fifth treatment. Closing it means the words as well as the marks:
 *    a row's label is the one its kind produces, and a title is an observation's headline
 *    drawn after that label rather than free text drawn instead of it.
 * 2. **A bookend has no place on the timeline.** It was recorded outside the capture
 *    window, so it is pinned to the head or the foot of the rail rather than sorted into
 *    the timecodes (`CONTEXT.md`). The enforceable form of that is not "remember to pin
 *    it": it is that a bookend carries no timecode field at all, so there is nothing for a
 *    sort to read.
 * 3. **A transcript line names who said it.** Story 31 is an export the owner takes to
 *    another tool, and a line with no name is a line that arrives there as an anonymous
 *    claim about somebody's work. The attribution is the person the session belongs to,
 *    carried on the port from the library row, and it is never inferred from the audio:
 *    one microphone and one known recorder is why the forbidden-symbol manifest bans the
 *    inference outright.
 *
 * **This is not a `wire` type.** It is the port an adapter fills, the way `SessionMedia` is.
 * `backend/adapter.ts` fills it from `GET /sessions/{id}/derived` (issue 228): the Summary's
 * paragraph and steps, and the Transcript's lines on the bookends. No route reads notes,
 * voiceovers or Observations back, so those lists stay empty from the adapter.
 */

/**
 * What kind of row this is, and therefore how it is marked.
 *
 * Four, and the fifth is a refusal rather than a variant. `bookend` is not in `RailEntry`'s
 * own `kind` because a bookend is not placed by a timecode; it is in this union because the
 * vocabulary names four kinds and a row is one of them.
 */
export type RailKind = "bookend" | "voiceover" | "observation" | "note";

/** Where a row came from, which is the only thing the accent is allowed to track. */
export type Provenance = "speech" | "model" | "human";

const RAIL_KIND_TABLE: Readonly<
  Record<RailKind, { readonly label: string; readonly from: Provenance }>
> = {
  bookend: { label: "Bookend", from: "speech" },
  voiceover: { label: "Voiceover", from: "speech" },
  observation: { label: "Observation", from: "model" },
  note: { label: "Note", from: "human" },
};

/** The four, as values, so membership is a walk over the vocabulary it names. */
export const RAIL_KINDS = Object.keys(RAIL_KIND_TABLE) as readonly RailKind[];

/**
 * Whether this is one of the four, and `includes` rather than `in` for a reason.
 *
 * `in` walks the prototype chain, so `"constructor"`, `"toString"` and `"__proto__"` are
 * all `in` an object literal: a predicate written that way closes nothing, and the sentence
 * above it saying the vocabulary is closed would be false. The vocabulary is these four
 * values and membership is a walk over them.
 */
export function isRailKind(value: unknown): value is RailKind {
  return typeof value === "string" && (RAIL_KINDS as readonly string[]).includes(value);
}

/** A row's word for its kind, in sentence case. Never the identifier: an owner reads copy. */
export function railKindLabel(kind: RailKind): string {
  return RAIL_KIND_TABLE[kind].label;
}

/**
 * Whether this kind came from speech, from a model, or from a person typing. Written on each
 * row as `data-rail-from`; it carries no color (cyan for AI is retired, ux-spec D10, D35).
 */
export function railKindFrom(kind: RailKind): Provenance {
  return RAIL_KIND_TABLE[kind].from;
}

/** The four `wire::ArtifactState` spellings, so an unknown one is refused not rendered. */
const ARTIFACT_STATES: readonly ArtifactState[] = [
  "present",
  "pending",
  "absent_by_design",
  "failed",
];

export function isArtifactState(value: unknown): value is ArtifactState {
  return typeof value === "string" && (ARTIFACT_STATES as readonly string[]).includes(value);
}

/** The three `wire::AbsentReason` spellings, refused rather than rendered when unknown. */
const ABSENT_REASONS: readonly AbsentReason[] = ["no_speech", "no_recording", "not_in_this_build"];

function isAbsentReason(value: unknown): value is AbsentReason {
  return typeof value === "string" && (ABSENT_REASONS as readonly string[]).includes(value);
}

/** The two artifacts the reading column states a state for. */
export type PanelArtifact = "Summary" | "Transcript";

/**
 * What a section says about an artifact that is not present (catalog `session-pending`,
 * `session-absent`, `session-failed`; design-spec §6). Three shapes, three words (P5):
 *
 * - `pending`: `Being written.`, and nothing about how long (P4).
 * - `absent`: says why, where the value establishes why. A build limit says so; no
 *   recording says so (issue 221); a transcript says `nobody spoke` only where `hasSpeech` is
 *   false (issue 85, issue 29's round-2 repair); otherwise the bare absence, no reason. A
 *   Summary never says nobody spoke: it is written from the screen too (issue 228).
 * - `failed`: what happened, then what is unaffected (F8).
 */
export type ArtifactCopy =
  | { readonly state: "pending"; readonly text: string }
  | { readonly state: "absent"; readonly reason: AbsentReason | null; readonly text: string }
  | { readonly state: "failed"; readonly text: string; readonly next: string };

export const BEING_WRITTEN = "Being written.";

export function artifactCopy(artifact: PanelArtifact, reading: Reading): ArtifactCopy | null {
  const summary = artifact === "Summary";
  const state = summary ? reading.summaryState : reading.transcriptState;
  if (state === "present") return null;
  if (state === "pending") return { state: "pending", text: BEING_WRITTEN };
  if (state === "failed") {
    return summary
      ? { state: "failed", text: "The summary didn't finish.", next: "The transcript and notes are unaffected." }
      : { state: "failed", text: "The transcript didn't finish.", next: "The summary and notes are unaffected." };
  }
  const reason = summary ? reading.summaryAbsentReason : reading.transcriptAbsentReason;
  const word = summary ? "summary" : "transcript";
  const words = summary ? "summaries" : "transcripts";
  // A Summary is written from the screen as well as speech (issue 221), so silence is never
  // why one is absent: only the transcript's absence may say nobody spoke.
  // A recording (issue 321) has segments and no microphone (ADR 0051), so its transcript's
  // absence is Don's, not a fact about whether anybody spoke (321 UX P1-1).
  const text =
    !summary && reading.segments.length > 0
      ? "No transcript: Don doesn't record sound."
      : reason === "not_in_this_build"
      ? `No ${word}: this build doesn't write ${words}.`
      : reason === "no_recording"
        ? `No ${word}: this session has no recording.`
        : !summary && !hasSpeech(reading)
          ? "Nobody spoke in this session."
          : `No ${word} for this session.`;
  return { state: "absent", reason, text };
}

/**
 * One voice bookend, spoken outside the capture window.
 *
 * **It carries no timecode**, and that is the whole of "pinned rather than sorted": there
 * is no field here for a comparator to read, so no later edit can accidentally place an
 * intro at `0` and let it tie with the first thing that happened.
 */
export interface Bookend {
  readonly which: "intro" | "outro";
  /**
   * The clip's own length, or `null` where the source does not say: the derived route's
   * transcript carries each line's offset into its clip and not the clip's length.
   */
  readonly durationMs: number | null;
  readonly text: string;
}

/**
 * Where a Summary step's evidence is: a time in the recording, or a bookend (`wire::Evidence`).
 * A bookend carries no time, for the reason `Bookend` carries none.
 */
export type StepAt = { readonly atMs: number } | { readonly bookend: "intro" | "outro" };

/** One step the person took, in the order the Summary gives them (issue 228). */
export interface SummaryStep {
  readonly id: string;
  readonly at: StepAt;
  readonly text: string;
}

/** A step with text for its words and a bookend or a finite, non-negative time for its place. */
function isWellFormedStep(step: SummaryStep): boolean {
  if (typeof step !== "object" || step === null || typeof step.text !== "string") return false;
  const at: unknown = step.at;
  if (typeof at !== "object" || at === null) return false;
  if ("bookend" in at) return at.bookend === "intro" || at.bookend === "outro";
  return "atMs" in at && typeof at.atMs === "number" && Number.isFinite(at.atMs) && at.atMs >= 0;
}

/**
 * A step's place, as the text the Summary prints beside it: the timecode, or `Before` /
 * `After`, the words the Transcript's bookend rows print for the same clips (one name for one
 * bookend on one page). Text only: a step's time is a model's estimate, so it does not seek
 * the player.
 */
export function stepAtText(step: SummaryStep): string {
  return atText(step.at);
}

/**
 * A place as text: the timecode, or `Before` / `After` for a bookend. A step's place and a
 * knowledge item's evidence (issue 229) both print through it, so one bookend has one name.
 */
export function atText(at: StepAt): string {
  if ("bookend" in at) return at.bookend === "intro" ? "Before" : "After";
  return timecode(at.atMs);
}

/** One timed row of the rail: the three kinds that happened during the capture window. */
export interface RailEntry {
  readonly kind: Exclude<RailKind, "bookend">;
  /** Where on the session timeline it happened. Clicking the row seeks here. */
  readonly atMs: number;
  /** A voiceover's clip length, or `null` for a row that is not a clip. */
  readonly durationMs: number | null;
  /**
   * An observation's own headline, or `null`.
   *
   * Only an observation has one, which `readingOf` refuses the other two kinds for, and it
   * is drawn after the kind's label rather than in place of it. Both halves are there
   * because the label slot is free text otherwise: see the refusal for what was measured
   * standing in it.
   */
  readonly title: string | null;
  readonly text: string;
}

/** One line of the transcript. `speaker` is who said it, never who a model guessed. */
export interface TranscriptLine {
  readonly atMs: number;
  readonly speaker: string;
  readonly text: string;
}

/** What the reading column is handed. The port a real adapter fills. */
export interface SessionReading {
  readonly sessionId: SessionId;
  /** The session timeline the timecodes are on, the same number `SessionMedia` carries. */
  readonly durationMs: number;
  /** The Summary's state, which is the rail tab's state: one of exactly four. */
  readonly summaryState: ArtifactState;
  /** The transcript's state, which is the transcript tab's and the export's. */
  readonly transcriptState: ArtifactState;
  /**
   * Which absence an `absent_by_design` Summary is, `wire::Artifact::absent_reason`
   * (issue 85). Optional on the port: missing reads as no reason, the ordinary absence.
   */
  readonly summaryAbsentReason?: AbsentReason | null;
  /** The same, for the transcript. */
  readonly transcriptAbsentReason?: AbsentReason | null;
  readonly intro: Bookend | null;
  readonly outro: Bookend | null;
  readonly entries: readonly RailEntry[];
  readonly transcript: readonly TranscriptLine[];
  /**
   * The Summary document's paragraph (issue 228), or `null`/missing where the source carries
   * no document. Read only under a `present` Summary.
   */
  readonly paragraph?: string | null;
  /** The Summary document's steps, in its order. Missing reads as none. */
  readonly steps?: readonly SummaryStep[];
  /**
   * A recording's Summaries, one per segment in ordinal order (issue 321). Missing on a
   * session. Where present, the Summary section lists these and not `paragraph` and `steps`.
   */
  readonly segments?: readonly SegmentReading[];
}

/**
 * One segment's Summary on a recording's timeline (issue 321): where it sits (`startMs` to
 * `endMs`, the route's offset and the segment's media), its state, and under `present` its
 * document, whose steps are already on the recording's clock (`backend/adapter.ts`).
 */
export interface SegmentReading {
  readonly ordinal: number;
  readonly sessionId: SessionId;
  readonly startMs: number;
  readonly endMs: number;
  readonly summaryState: ArtifactState;
  readonly summaryAbsentReason: AbsentReason | null;
  readonly paragraph: string | null;
  readonly steps: readonly SummaryStep[];
}

/**
 * What the side column is handed: a reading, `null` for one not read yet, or `"failed"` for a
 * reading read that did not answer (issue 202, catalog `session-reading-failed`).
 */
export type ReadingPort = SessionReading | "failed" | null;

/**
 * The rail, in the order it renders.
 *
 * Three fields rather than one list, because the order is a product rule and not a sort:
 * the intro is above everything that happened, the outro below it, and `entries` is the
 * only part with timecodes in it.
 */
export interface Rail {
  readonly head: Bookend | null;
  readonly entries: readonly RailEntry[];
  readonly foot: Bookend | null;
}

/** A validated reading. Nothing constructs one but `readingOf`. */
export interface Reading {
  readonly sessionId: SessionId;
  readonly durationMs: number;
  readonly summaryState: ArtifactState;
  readonly transcriptState: ArtifactState;
  /** Set only on an `absent_by_design` Summary; `null` otherwise (`readingOf`). */
  readonly summaryAbsentReason: AbsentReason | null;
  /** Set only on an `absent_by_design` transcript; `null` otherwise. */
  readonly transcriptAbsentReason: AbsentReason | null;
  readonly rail: Rail;
  readonly transcript: readonly TranscriptLine[];
  /** The Summary's paragraph: `null` unless the Summary is `present` with a document. */
  readonly paragraph: string | null;
  /** The Summary's steps, in the document's order; empty unless the Summary is `present`. */
  readonly steps: readonly SummaryStep[];
  /**
   * A recording's Summaries in ordinal order (issue 321), each document kept only under its own
   * `present`, as `paragraph` and `steps` are. Empty on a session.
   */
  readonly segments: readonly SegmentReading[];
  /** The employee's notes, which are the rail's `note` rows and not a second list. */
  readonly notes: readonly RailEntry[];
  /**
   * The Observations, which are the rail's `observation` rows and not a second list.
   *
   * Here as a field of their own because they are a separate artifact from the Summary,
   * with a different provenance (`CONTEXT.md`): they are derived from what the screen
   * showed and exist on a session nobody spoke during. The Summary tab draws the rail, so
   * without this the Summary's state decided whether a second artifact appeared at all.
   */
  readonly observations: readonly RailEntry[];
}

/**
 * Why a reading was refused.
 *
 * A value rather than a thrown error, as `TimelineFault` is: the column renders the
 * refusal, and a render that threw would take the player beside it down too.
 */
export interface ReadingFault {
  readonly fault:
    | "unknown-rail-kind"
    | "unknown-artifact-state"
    | "unknown-absent-reason"
    | "unattributed-line"
    | "absent-over-speech"
    | "not-text"
    | "titled-non-observation"
    | "non-finite"
    | "outside-session"
    | "not-a-rail";
  readonly detail: string;
}

export function isReading(value: Reading | ReadingFault): value is Reading {
  return !("fault" in value);
}

/**
 * The reading, or why there is not one.
 *
 * > **The invariant.** Every row this column renders carries a place, a source and its own
 * > words. A bookend's place is outside the capture window, which it states as `before` or
 * > `after` and never as a timecode; every other row's place is a timecode inside
 * > `[0, durationMs]`, and `rail.entries` is ascending by it; every row's source is one of
 * > the four kinds, and only an observation carries a headline of its own; every row's
 * > words are text, and so are a transcript line's; every transcript line names the
 * > person who said it; both artifact
 * > states are `wire::ArtifactState`'s own four spellings; and an `absent_by_design`
 * > transcript is over no lines. A `present` Summary is not checked against speech: it is
 * > written from the screen as well (issue 221), so a silent session may have one.
 *
 * **What is not in it, and why not.** `absent_by_design` is not read as "nobody spoke".
 * `CONTEXT.md` and `crates/wire/src/media.rs` both define it as a correct absence, *most
 * often* that one, and a check that refuses every other reason is a domain assumption
 * rather than a validation: the port carries no reason field to tell them apart with, so
 * refusing on the common reason's absence takes the transcript, the notes and the
 * Observations off the screen over writing that is fine. The assurance that needed
 * checking was the sentence, so the sentence is what changed: the panel states that there
 * is no Summary, and says why only where `hasSpeech` is false, which is the one reason
 * this value can establish on its own. The transcript's half stays a refusal because it is
 * not a claim about a reason: lines the value holds and a state saying nothing was
 * transcribed are the same manifest disagreeing with itself.
 *
 * As in `timelineOf`, the last step checks that on the value about to be returned rather
 * than on the shapes anyone enumerated, so a producer nobody predicted is refused rather
 * than drawn. `not-a-rail` is the backstop: reaching it means a named fault above it
 * should have fired first.
 */
export function readingOf(reading: SessionReading): Reading | ReadingFault {
  // --- 1. The domain.
  if (!Number.isFinite(reading.durationMs)) {
    return { fault: "non-finite", detail: `session durationMs is ${reading.durationMs}` };
  }
  for (const [which, state] of [
    ["Summary", reading.summaryState],
    ["transcript", reading.transcriptState],
  ] as const) {
    if (!isArtifactState(state)) {
      return {
        fault: "unknown-artifact-state",
        detail: `the ${which} is ${JSON.stringify(state)}, which is not one of the four`,
      };
    }
  }

  for (const [which, reason] of [
    ["Summary", reading.summaryAbsentReason],
    ["transcript", reading.transcriptAbsentReason],
  ] as const) {
    if (reason !== undefined && reason !== null && !isAbsentReason(reason)) {
      return {
        fault: "unknown-absent-reason",
        detail: `the ${which} is absent for ${JSON.stringify(reason)}, which is not one of the two`,
      };
    }
  }

  for (const bookend of [reading.intro, reading.outro]) {
    if (bookend === null) continue;
    if (bookend.durationMs !== null && (!Number.isFinite(bookend.durationMs) || bookend.durationMs < 0)) {
      return {
        fault: "non-finite",
        detail: `the ${bookend.which} clip is ${bookend.durationMs}ms long`,
      };
    }
    if (typeof bookend.text !== "string") {
      return {
        fault: "not-text",
        detail: `the ${bookend.which} clip's text is ${typeName(bookend.text)}`,
      };
    }
  }

  for (const entry of reading.entries) {
    // A fifth kind is the shape this refusal exists for. Decisions §(g) cut `PRIVATE`, and
    // the design bundle still ships it, so an adapter written from the bundle rather than
    // from the decision is the ordinary producer of one. Rendering it would put an
    // owner-only row on a screen with no owner-only access rule behind it.
    // Widened on purpose: the static type already excludes both cases, and the producer
    // is an adapter that has no types at all. A guard the compiler thinks is unnecessary is
    // exactly the guard a runtime value needs.
    const kind: string = entry.kind;
    if (!isRailKind(kind) || kind === "bookend") {
      return {
        fault: "unknown-rail-kind",
        detail: `a row at ${entry.atMs}ms is ${JSON.stringify(entry.kind)}`,
      };
    }
    if (!Number.isFinite(entry.atMs)) {
      return { fault: "non-finite", detail: `a ${entry.kind} row is at ${entry.atMs}ms` };
    }
    if (entry.atMs < 0 || entry.atMs > reading.durationMs) {
      return {
        fault: "outside-session",
        detail: `a ${entry.kind} row at ${entry.atMs}ms is outside 0ms–${reading.durationMs}ms`,
      };
    }
    // A row's own words. Unchecked, a value that is not text is accepted here and thrown
    // by the render, which takes the whole screen with it rather than this column: that is
    // the outcome the fault-as-value shape above exists to prevent, so the fields it is
    // written about have to be the fields it checks.
    if (typeof entry.text !== "string") {
      return {
        fault: "not-text",
        detail: `a ${kind} row at ${entry.atMs}ms has text that is ${typeName(entry.text)}`,
      };
    }
    // **A title is an observation's headline and never a label.** The old rail
    // component (`TimelineEntry`, deleted in issue 205) drew `title || labels[kind]`, so an unchecked title stands where the closed vocabulary's
    // own word should be: a `note` titled `YOUR PRIVATE NOTE · NOBODY ELSE SEES THIS`
    // renders the design bundle's fifth-kind label under `data-rail-kind="note"`, which is
    // the string decisions §(g) cut and the `[P0]` reads that attribute to keep off this
    // screen, and one titled `VOICEOVER` calls a typed note speech while `data-rail-from`
    // still says `human`. Closing a vocabulary and leaving an override beside it makes the
    // closure decorative. Two halves close it: this refusal, and the render, which draws a
    // title after the kind's label rather than in place of it.
    if (entry.title !== null) {
      if (typeof entry.title !== "string" || entry.title.trim().length === 0) {
        return {
          fault: "not-text",
          detail: `a ${kind} row at ${entry.atMs}ms has a title that is ${typeName(entry.title)}`,
        };
      }
      if (kind !== "observation") {
        return {
          fault: "titled-non-observation",
          detail: `a ${kind} row at ${entry.atMs}ms carries a title, which only an observation has`,
        };
      }
    }
  }

  for (const line of reading.transcript) {
    // The transcript's version of `CONTEXT.md`'s rule that a gap always carries its
    // reason. An unnamed line exports as an anonymous claim about somebody's work, and the
    // export is story 31's whole point.
    if (typeof line.speaker !== "string" || line.speaker.trim().length === 0) {
      return {
        fault: "unattributed-line",
        detail: `the line at ${line.atMs}ms names nobody`,
      };
    }
    if (!Number.isFinite(line.atMs)) {
      return { fault: "non-finite", detail: `a transcript line is at ${line.atMs}ms` };
    }
    if (line.atMs < 0 || line.atMs > reading.durationMs) {
      return {
        fault: "outside-session",
        detail: `a transcript line at ${line.atMs}ms is outside 0ms–${reading.durationMs}ms`,
      };
    }
    // A line's words, for the reason the rows' words are checked above and for one more
    // that is this field's alone. The render puts `line.text` on the Transcript tab as a
    // React child, so a value that is not text empties the screen on one click; and
    // `transcriptText` puts it into the owner's clipboard under the name the check above
    // insists on, so an unchecked body is a timestamped, attributed claim whose words
    // nobody said. Refusing the name and not the words holds half of the property
    // `unattributed-line` is written for.
    if (typeof line.text !== "string") {
      return {
        fault: "not-text",
        detail: `the line at ${line.atMs}ms has text that is ${typeName(line.text)}`,
      };
    }
  }

  // The Summary document. Its words are model output, so they are checked as text here and
  // rendered as text nodes only; a step's place is a time inside the session or a bookend.
  const paragraph = reading.paragraph ?? null;
  if (paragraph !== null && typeof paragraph !== "string") {
    return { fault: "not-text", detail: `the Summary's paragraph is ${typeName(paragraph)}` };
  }
  // A step is a model's estimate rather than a recorded row, so one malformed step is dropped
  // on its own instead of refusing the column: its Transcript and notes have nothing wrong
  // with them. No upper bound: the time is display text that never seeks, the Console's
  // `durationMs` is 0 once the video has expired while the writing outlives it, and the server
  // already drops an `at` outside the recording (issue 224).
  const steps = (reading.steps ?? []).filter(isWellFormedStep);

  // A recording's Summaries (issue 321): each segment's state and range checked as the
  // session's are, its document kept only under its own `present`, a malformed step dropped.
  const segments: SegmentReading[] = [];
  for (const segment of reading.segments ?? []) {
    if (!isArtifactState(segment.summaryState)) {
      return {
        fault: "unknown-artifact-state",
        detail: `segment ${segment.ordinal}'s Summary is ${JSON.stringify(segment.summaryState)}`,
      };
    }
    if (segment.summaryAbsentReason !== null && !isAbsentReason(segment.summaryAbsentReason)) {
      return {
        fault: "unknown-absent-reason",
        detail: `segment ${segment.ordinal}'s Summary is absent for ${JSON.stringify(segment.summaryAbsentReason)}`,
      };
    }
    if (!Number.isFinite(segment.startMs) || !Number.isFinite(segment.endMs) || segment.endMs < segment.startMs) {
      return { fault: "non-finite", detail: `segment ${segment.ordinal} spans ${segment.startMs}ms–${segment.endMs}ms` };
    }
    if (segment.paragraph !== null && typeof segment.paragraph !== "string") {
      return { fault: "not-text", detail: `segment ${segment.ordinal}'s paragraph is ${typeName(segment.paragraph)}` };
    }
    const present = segment.summaryState === "present";
    segments.push({
      ...segment,
      summaryAbsentReason: segment.summaryState === "absent_by_design" ? segment.summaryAbsentReason : null,
      paragraph: present ? segment.paragraph : null,
      steps: present ? segment.steps.filter(isWellFormedStep) : [],
    });
  }

  // --- 2. The value. The bookends are not in `entries` and never were.
  const entries = reading.entries.slice().sort((a, b) => a.atMs - b.atMs);
  const built: Reading = {
    sessionId: reading.sessionId,
    durationMs: reading.durationMs,
    summaryState: reading.summaryState,
    transcriptState: reading.transcriptState,
    // A reason belongs to an absence, so it is dropped from any other state rather than
    // carried where nothing reads it.
    summaryAbsentReason:
      reading.summaryState === "absent_by_design" ? (reading.summaryAbsentReason ?? null) : null,
    transcriptAbsentReason:
      reading.transcriptState === "absent_by_design"
        ? (reading.transcriptAbsentReason ?? null)
        : null,
    rail: { head: reading.intro, entries, foot: reading.outro },
    transcript: reading.transcript.slice().sort((a, b) => a.atMs - b.atMs),
    // A document belongs to a present Summary, as a reason belongs to an absence.
    paragraph: reading.summaryState === "present" ? paragraph : null,
    steps: reading.summaryState === "present" ? steps.slice() : [],
    segments,
    notes: entries.filter((entry) => entry.kind === "note"),
    observations: entries.filter((entry) => entry.kind === "observation"),
  };

  // --- 3. The invariant, checked on the value rather than on the inputs.
  let previous = -1;
  for (const entry of built.rail.entries) {
    const kind: string = entry.kind;
    if (entry.atMs < previous || !isRailKind(kind) || kind === "bookend") {
      return {
        fault: "not-a-rail",
        detail: `a ${String(entry.kind)} row at ${entry.atMs}ms does not follow ${previous}ms`,
      };
    }
    previous = entry.atMs;
  }
  // **The Summary's state is not checked against speech, in either direction.** A present
  // Summary over a silent session is a screen-only Summary (issue 221), and an absent one is
  // not refused over speech either: a refusal is the whole reading, so it would cost the
  // owner the transcript, the notes and the Observations as well.
  //
  // The transcript's check is `transcript` rather than `hasSpeech` on purpose:
  // a bookend is spoken outside the capture window and carries its own text, so a session
  // with an intro and nothing said during the capture has no transcript and that is not a
  // contradiction. Lines on the value under a state saying nothing was transcribed are not
  // a reason nobody can check: they are the same manifest disagreeing with itself.
  if (built.transcriptState === "absent_by_design" && built.transcript.length > 0) {
    return {
      fault: "absent-over-speech",
      detail: "the transcript is absent by design over lines that exist",
    };
  }
  if (built.rail.head !== null && built.rail.head.which !== "intro") {
    return { fault: "not-a-rail", detail: "the row at the head of the rail is not the intro" };
  }
  if (built.rail.foot !== null && built.rail.foot.which !== "outro") {
    return { fault: "not-a-rail", detail: "the row at the foot of the rail is not the outro" };
  }
  return built;
}

/**
 * What a field that should have been text is instead, so the `detail` reads true.
 *
 * `blank` is the string case, because a title of `"   "` is the one value that is text and
 * still not a headline: it renders as a label slot with nothing in it.
 */
function typeName(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "string") return "blank";
  return typeof value === "object" || typeof value === "undefined"
    ? `an ${typeof value}`
    : `a ${typeof value}`;
}

/**
 * Whether there is any speech to write a Summary from.
 *
 * The absent-by-design case, and it is the common one rather than an error: the microphone
 * is off by default mid-session, so a session whose intro and outro were both skipped and
 * whose owner never held the hotkey produced no speech at all. The screen says that in a
 * sentence instead of drawing an empty rail, which would read as a Summary that failed.
 *
 * One reader: `artifactCopy`, for the transcript's absence. Over `false` that absence may
 * say nobody spoke; over `true` it states no reason, because this value cannot tell a
 * transcript absent for another allowed reason from one absent wrongly. The Summary reads
 * neither way: it is written from the screen as well as speech (issues 221, 228).
 */
export function hasSpeech(reading: Reading): boolean {
  return (
    reading.rail.head !== null ||
    reading.rail.foot !== null ||
    reading.rail.entries.some((entry) => entry.kind === "voiceover") ||
    reading.transcript.length > 0
  );
}

/** What the head and foot of the rail say where every other row says a timecode. */
export const BOOKEND_TIMES: Readonly<Record<"intro" | "outro", string>> = {
  intro: "before",
  outro: "after",
};

/**
 * The transcript as plain text: a timecode, the speaker, and what they said.
 *
 * V0's substitute for in-product chat (story 31), so it is what the owner pastes into
 * another tool. Two properties the format has to have and the `[P0]` asserts: a timestamp
 * on every line, so a claim can be taken back to the moment it came from, and a name on
 * every line, so it cannot be pasted somewhere as an unattributed claim about somebody's
 * work. `readingOf` is what makes the second one free rather than a formatting rule: a
 * line with no name is not a value this function can be handed.
 *
 * It is the lines the Transcript tab shows and nothing else, so what is copied is what is
 * on the screen. The bookends are spoken outside the capture window and have no timecode
 * to carry, so putting them in a timestamped export would need a timestamp invented for
 * them.
 */
export function transcriptText(reading: Reading): string {
  return reading.transcript
    .map((line) => `${timecode(line.atMs)}  ${line.speaker}: ${line.text}`)
    .join("\n");
}

/** Whether there is a transcript to copy or download: present, and with lines in it. */
export function transcriptExportable(reading: Reading): boolean {
  return reading.transcriptState === "present" && reading.transcript.length > 0;
}

/** What a downloaded transcript is called. The session, so two downloads do not collide. */
export function transcriptFilename(reading: Reading): string {
  return `transcript-${reading.sessionId}.txt`;
}

/**
 * One row of the Transcript section (design-spec §4 "Transcript row", variant E `.ve-tr`): the
 * intro and outro pinned at the head and foot (they have no timecode), and between them, by
 * position, what was said, the notes, the Observations and the gaps.
 */
export type TranscriptRow =
  | { readonly kind: "bookend"; readonly which: "intro" | "outro"; readonly speaker: string | null; readonly text: string }
  | { readonly kind: "speech"; readonly atMs: number; readonly speaker: string; readonly text: string }
  | { readonly kind: "note"; readonly atMs: number; readonly text: string }
  | { readonly kind: "observation"; readonly atMs: number; readonly title: string | null; readonly text: string }
  | { readonly kind: "gap"; readonly atMs: number; readonly label: string };

/**
 * The Transcript section's rows. A gap reads in the one form every gap has, `duration ·
 * cause` (`gapText`, P5); a bookend names the person the session belongs to, the attribution
 * the transcript lines carry, or nobody when the screen does not know who that is.
 */
export function transcriptRows(
  reading: Reading,
  gaps: readonly SessionGap[] = [],
  person?: string,
): TranscriptRow[] {
  const timed: TranscriptRow[] = [
    ...reading.transcript.map((line) => ({ kind: "speech" as const, atMs: line.atMs, speaker: line.speaker, text: line.text })),
    ...reading.notes.map((note) => ({ kind: "note" as const, atMs: note.atMs, text: note.text })),
    ...reading.observations.map((row) => ({ kind: "observation" as const, atMs: row.atMs, title: row.title, text: row.text })),
    ...gaps.map((gap) => ({ kind: "gap" as const, atMs: gap.startMs, label: gapText(gap, person) })),
  ];
  // `sort` is stable, so rows at one instant keep the order above.
  timed.sort((a, b) => ("atMs" in a ? a.atMs : 0) - ("atMs" in b ? b.atMs : 0));
  const bookend = (b: Bookend): TranscriptRow => ({ kind: "bookend", which: b.which, speaker: person ?? null, text: b.text });
  return [
    ...(reading.rail.head === null ? [] : [bookend(reading.rail.head)]),
    ...timed,
    ...(reading.rail.foot === null ? [] : [bookend(reading.rail.foot)]),
  ];
}
